import { RFQSweepingButton } from '@src/components/OMS/NewRFQ/QuoteSweeping';
import { securityHasSizeCounteringFeatureFlagEnabled } from '@src/components/OMS/NewRFQ/utils';
import { useUser } from '@src/hooks/useUser';
import { useAppStateSelector } from '@src/providers/AppStateProvider';
import {
  AgGridHamburgerMenu,
  Box,
  Button,
  ButtonVariants,
  createBlotterTableNativeClickRef,
  FormControlSizes,
  HStack,
  IconName,
  SideEnum,
  Tooltip,
  type ColumnDef,
  type Quote,
} from '@talos/kyoko';
import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { QuoteAcceptButton } from './QuoteAcceptButton/QuoteAcceptButton';
import { QuoteMarketPartialFillButton } from './QuoteMarketPartialFillButton';
import { QuoteMarketRow, QuoteRow, SweepGroupIDRow, type QuotesBlotterRow } from './types';
import { getQuoteOpenButtonDisabledReason } from './utils';

export interface UseQuotesV2BlotterActionColumnParams {
  onOpenQuote: (quote: Quote) => void;
  onShowDetails: (quote: Quote) => void;
}

export const useQuotesV2BlotterActionColumn = ({
  onOpenQuote,
  onShowDetails,
}: UseQuotesV2BlotterActionColumnParams) => {
  const user = useUser();
  const {
    enableRFQSweeping,
    enableRFQSizeCounteringMultitokenCompound,
    enableRFQSizeCounteringSpot,
    sweepGroupIDQuotesOnlySweepable,
    enableExecutingOtherUserNegotiatedRFQ,
  } = useFeatureFlag();
  const securitiesBySymbol = useAppStateSelector(state => state.referenceData.securities.securitiesBySymbol);

  const column: ColumnDef<QuotesBlotterRow> = useMemo(() => {
    return {
      type: 'custom',
      id: 'action',
      title: 'Actions',
      exportable: false,
      initialWidth: 190,
      maxWidth: 190,
      minWidth: 190,

      params: {
        suppressColumnsToolPanel: true,
        suppressAutoSize: true,
        sortable: false,
        resizable: false,
        initialPinned: 'right',
        lockVisible: true,
        lockPosition: true,
        lockPinned: true,
        valueGetter: (params: ValueGetterParams<QuotesBlotterRow>): QuotesBlotterRow | undefined => {
          return params.data; // we have the entire data row as our value so we always trigger change detection
        },
        cellRenderer: (params: ICellRendererParams<QuotesBlotterRow, QuotesBlotterRow>) => {
          const entity = params.value;
          if (!entity) {
            return null;
          }

          if (entity instanceof SweepGroupIDRow) {
            if (entity.SweepGroupID === SweepGroupIDRow.unassignedValue) {
              return null;
            }

            const symbol = entity.quotes.at(0)?.Symbol;
            const security = symbol ? securitiesBySymbol.get(symbol) : undefined;
            const canSweep =
              enableRFQSweeping &&
              securityHasSizeCounteringFeatureFlagEnabled(security, {
                enableRFQSizeCounteringMultitokenCompound,
                enableRFQSizeCounteringSpot,
              });
            if (!canSweep) {
              return null;
            }

            return (
              <HStack w="100%" justifyContent="flex-end">
                <RFQSweepingButton
                  correlationID={entity.SweepGroupID}
                  disabled={entity.isDisabled}
                  size={FormControlSizes.Small}
                  variant={ButtonVariants.Primary}
                  data-testid="quotes-v2-action-button-sweep"
                  startIcon={IconName.Scale}
                >
                  Sweep
                </RFQSweepingButton>
              </HStack>
            );
          }

          if (entity instanceof QuoteMarketRow) {
            return (
              <QuoteMarketPartialFillButton
                quote={entity.parentQuote}
                quoteMarket={entity.quoteMarket}
                careOrderSide={entity.careOrderSide}
                associatedCareOrder={entity.parentQuote.careOrder}
              />
            );
          }

          if (!(entity instanceof QuoteRow)) {
            return null;
          }

          const { quote, careOrderSide } = entity;
          const openButtonDisabledReason = getQuoteOpenButtonDisabledReason({
            quote,
            user,
            sweepGroupIDQuotesOnlySweepable,
            enableExecutingOtherUserNegotiatedRFQ,
          });
          const renderMenuItems = () => (
            <Button
              size={FormControlSizes.Small}
              onClick={() => onShowDetails(quote)}
              data-testid="quotes-v2-action-button-details"
            >
              Details
            </Button>
          );

          return (
            <HStack gap="spacing4" w="100%" justifyContent="flex-start">
              <QuoteAcceptButton
                quote={quote}
                side={SideEnum.Sell}
                bestMarket={entity.bid}
                careOrderSide={careOrderSide}
              />
              <QuoteAcceptButton
                quote={quote}
                side={SideEnum.Buy}
                bestMarket={entity.offer}
                careOrderSide={careOrderSide}
              />
              <Box flex="1">
                <Tooltip
                  tooltip={openButtonDisabledReason}
                  tooltipTestID="quotes-v2-action-button-open-disabled-tooltip"
                >
                  <Button
                    width="100%"
                    size={FormControlSizes.Small}
                    variant={ButtonVariants.Default}
                    disabled={openButtonDisabledReason != null}
                    ref={createBlotterTableNativeClickRef<HTMLButtonElement>({
                      onClick: () => onOpenQuote(quote),
                    })}
                    data-testid="quotes-v2-action-button-open"
                  >
                    Open
                  </Button>
                </Tooltip>
              </Box>
              <AgGridHamburgerMenu
                renderItems={renderMenuItems}
                {...params}
                iconButtonProps={{
                  size: FormControlSizes.Small,
                  ghost: false,
                }}
              />
            </HStack>
          );
        },
      } satisfies ColDef,
    };
  }, [
    enableRFQSweeping,
    enableRFQSizeCounteringMultitokenCompound,
    enableRFQSizeCounteringSpot,
    enableExecutingOtherUserNegotiatedRFQ,
    onOpenQuote,
    onShowDetails,
    securitiesBySymbol,
    sweepGroupIDQuotesOnlySweepable,
    user,
  ]);

  return column;
};
